import { defineStore } from 'pinia';
import { ref } from 'vue';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from './auth';
import { mapEventFromDb, mapEventAttendeeFromDb, mapEventFormatToDb } from '../utils/mappers';
import { getBaseCapacity, getEventLocationName } from '../utils/eventFormat';
import { AnalyticsEvents } from '../lib/analytics';
import { useSubscription } from '../composables/useSubscription';
import { fetchAllPages } from '../utils/supabasePagination';

export const useEventStore = defineStore('events', () => {
  const events = ref([]);
  const loading = ref(false);
  const error = ref(null);
  
  // Track in-flight fetch to prevent duplicates
  let fetchEventsInProgress = false;
  
  // Cache control - prevent redundant fetches within a short time window
  let lastFetchTime = null;
  const CACHE_DURATION_MS = 30000; // 30 seconds

  async function getEventAttendees(eventId) {
    try {
      const { data: allEventAttendees, error: pageError } = await fetchAllPages((from, to) =>
        supabase
          .from('event_attendees')
          .select(`
            id,
            attendee_id,
            checked_in,
            checked_in_at,
            checked_in_by,
            interested_in_appointment,
            interested_in_appointment_at,
            interested_in_appointment_by,
            confirmed,
            confirmed_at,
            confirmed_by,
            confirmation_source,
            evaluation_completed,
            evaluation_completed_at,
            evaluation_completed_by,
            status,
            waitlist_position,
            notification_preferences,
            revenue,
            revenue_date,
            _created_at,
            _created_by,
            attendees!inner (
              id,
              first_name,
              last_name,
              email,
              phone_number,
              street_address,
              city,
              state,
              zip_code,
              notes,
              referral_source,
              gender,
              relationship,
              host_id
            ),
            appointments (
              id,
              calendar_name,
              appointment_date,
              appointment_time,
              notes,
              created_at,
              updated_at,
              _created_by,
              _deleted
            )
          `)
          .eq('event_id', eventId)
          .eq('_deleted', false)
          .range(from, to)
      );

      if (pageError) throw pageError;

      // Filter out records where the attendee join failed (e.g., attendee was soft-deleted)
      const validEventAttendees = (allEventAttendees || []).filter(ea => {
        if (!ea.attendees) {
          console.warn(`Event attendee ${ea.id} has no linked attendee (may be soft-deleted), skipping`);
          return false;
        }
        return true;
      });

      if (validEventAttendees.length < (allEventAttendees || []).length) {
        console.warn(`Filtered out ${(allEventAttendees || []).length - validEventAttendees.length} orphaned event_attendee records`);
      }

      return {
        data: validEventAttendees.map(mapEventAttendeeFromDb).filter(Boolean),
        error: null
      };
    } catch (err) {
      console.error('Error fetching event attendees:', err);
      return { data: [], error: err };
    }
  }

  async function updateEventAttendee(eventId, attendeeId, updates) {
    try {
      const authStore = useAuthStore();
      const userId = authStore.user?.id;
      const now = new Date().toISOString();
      
      const updateData = {
        checked_in: updates.checkedIn,
        interested_in_appointment: updates.interestedInAppointment,
        confirmed: updates.confirmed,
        confirmation_source: updates.confirmationSource
      };

      // Track who and when for checked_in changes
      if (updates.checkedIn !== undefined) {
        if (updates.checkedIn) {
          updateData.checked_in_at = now;
          updateData.checked_in_by = userId;
        } else {
          updateData.checked_in_at = null;
          updateData.checked_in_by = null;
        }
      }

      // Track who and when for confirmed changes
      if (updates.confirmed !== undefined) {
        if (updates.confirmed) {
          updateData.confirmed_at = now;
          updateData.confirmed_by = userId;
        } else {
          updateData.confirmed_at = null;
          updateData.confirmed_by = null;
        }
      }

      // Track who and when for interested_in_appointment changes
      if (updates.interestedInAppointment !== undefined) {
        if (updates.interestedInAppointment) {
          updateData.interested_in_appointment_at = now;
          updateData.interested_in_appointment_by = userId;
        } else {
          updateData.interested_in_appointment_at = null;
          updateData.interested_in_appointment_by = null;
        }
      }

      // Track who and when for evaluation_completed changes
      if (updates.evaluationCompleted !== undefined) {
        updateData.evaluation_completed = updates.evaluationCompleted;
        if (updates.evaluationCompleted) {
          updateData.evaluation_completed_at = now;
          updateData.evaluation_completed_by = userId;
        } else {
          updateData.evaluation_completed_at = null;
          updateData.evaluation_completed_by = null;
        }
      }

      // Only include status and waitlist_position if they are provided
      if (updates.status !== undefined) {
        updateData.status = updates.status;
      }
      if (updates.waitlistPosition !== undefined) {
        updateData.waitlist_position = updates.waitlistPosition;
      }
      if (updates.notification_preferences !== undefined) {
        updateData.notification_preferences = updates.notification_preferences;
      }

      const { error: err } = await supabase
        .from('event_attendees')
        .update(updateData)
        .eq('event_id', eventId)
        .eq('attendee_id', attendeeId);

      if (err) throw err;

      return { error: null };
    } catch (err) {
      console.error('Error updating event attendee:', err);
      return { error: err };
    }
  }

  async function fetchEvents(forceRefresh = false) {
    // Prevent duplicate calls while already fetching
    if (fetchEventsInProgress) {
      console.log('⏳ fetchEvents: Already in progress, skipping duplicate call');
      return;
    }
    
    // Check cache - skip fetch if data is fresh (unless forced)
    if (!forceRefresh && lastFetchTime && events.value.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION_MS) {
        console.log(`⚡ fetchEvents: Using cached data (${Math.round(timeSinceLastFetch / 1000)}s old)`);
        return;
      }
    }
    
    fetchEventsInProgress = true;
    
    try {
      loading.value = true;
      error.value = null;

      // RLS scopes rows to the caller's organization. The explicit filter
      // keeps a foreign event out of the list if that policy is ever loosened.
      const authStore = useAuthStore();
      const orgId = authStore.organizationId;
      const { data: allEventsData, error: pageError } = await fetchAllPages((from, to) => {
        let query = supabase
          .from('events')
          .select(`
            *,
            campaigns (
              name
            ),
            locations (
              name,
              address,
              capacity,
              office_id
            ),
            event_staff (
              name,
              role,
              team_member_id
            )
          `)
          .eq('_deleted', false)
          .order('date', { ascending: false });
        if (orgId) query = query.eq('organization_id', orgId);
        return query.range(from, to);
      });

      if (pageError) throw pageError;

      const eventsRows = (allEventsData || []).filter(event => !orgId || event.organization_id === orgId);

      // Map events with location and campaign details
      const mappedEvents = eventsRows.map(event => ({
        ...mapEventFromDb(event),
        locationName: getEventLocationName(event, event.locations) || null,
        locationCapacity: getBaseCapacity(event, event.locations),
        campaignName: event.campaigns?.name || null
      }));

      // Fetch event stats using the optimized RPC function
      const eventIds = eventsRows.map(e => e.id);
      if (eventIds.length > 0) {
        const { data: statsData, error: statsErr } = await supabase
          .rpc('get_event_stats', { p_event_ids: eventIds });
        
        if (statsErr) {
          console.error('Error fetching event stats:', statsErr);
        } else if (statsData) {
          // Create a map for quick lookup
          const statsMap = {};
          statsData.forEach(stat => {
            statsMap[stat.event_id] = {
              responders: Number(stat.responders),
              rsvps: Number(stat.rsvps),
              waitlist: Number(stat.waitlist),
              confirmations: Number(stat.confirmations),
              attendees: Number(stat.attendees),
              units: Number(stat.units),
              unitsYes: Number(stat.units_yes),
              appointments: Number(stat.appointments),
              evaluations: Number(stat.evaluations),
              engagements: Number(stat.engagements),
            };
          });
          
          // Attach stats to events
          mappedEvents.forEach(event => {
            event.stats = statsMap[event.id] || {
              responders: 0,
              rsvps: 0,
              waitlist: 0,
              confirmations: 0,
              attendees: 0,
              units: 0,
              unitsYes: 0,
              appointments: 0,
              evaluations: 0,
              engagements: 0,
            };
          });
        }
      }
      
      events.value = mappedEvents;
      lastFetchTime = Date.now();
    } catch (err) {
      error.value = 'Failed to fetch events';
      console.error('Error:', err);
    } finally {
      loading.value = false;
      fetchEventsInProgress = false;
    }
  }
  
  // Force refresh from server (invalidates cache)
  async function refreshEvents() {
    return fetchEvents(true);
  }

  async function createEvent(event) {
    try {
      loading.value = true;
      error.value = null;
      
      const authStore = useAuthStore();
      if (!authStore.organizationId) throw new Error('No organization selected');

      const { data, error: err } = await supabase
        .from('events')
        .insert([{
          name: event.name,
          date: event.date.toISOString(),
          presenter: event.presenter,
          registration_url: event.registrationUrl,
          ...mapEventFormatToDb(event),
          campaign_id: event.campaignId,
          food_option: event.foodOption || 'none',
          capacity_buffer: event.capacityBuffer || 0,
          notes: event.notes || null,
          organization_id: authStore.organizationId,
          user_id: authStore.user?.id
        }])
        .select()
        .single();

      if (err) throw err;

      // Save support staff (coordinators)
      if (event.supportStaff && event.supportStaff.length > 0) {
        const staffRecords = event.supportStaff.map(staff => ({
          event_id: data.id,
          name: staff.name,
          role: staff.role || 'Coordinator',
          team_member_id: staff.teamMemberId || null
        }));
        
        await supabase.from('event_staff').insert(staffRecords);
      }

      events.value.push(mapEventFromDb(data));
      AnalyticsEvents.eventCreated({ campaign_id: event.campaignId || '' });

      // Track usage for subscription limits
      try {
        const { incrementUsageCounter } = useSubscription();
        await incrementUsageCounter('events_created');
      } catch (_) { /* non-fatal */ }

      return data;
    } catch (err) {
      error.value = 'Failed to create event';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateEvent(event) {
    try {
      loading.value = true;
      error.value = null;

      const { error: err } = await supabase
        .from('events')
        .update({
          name: event.name,
          date: event.date.toISOString(),
          presenter: event.presenter,
          registration_url: event.registrationUrl,
          ...mapEventFormatToDb(event),
          campaign_id: event.campaignId,
          food_option: event.foodOption || 'none',
          capacity_buffer: event.capacityBuffer || 0,
          notes: event.notes || null,
          impact_notes: event.impactNotes || null
        })
        .eq('id', event.id);

      if (err) throw err;

      // Update support staff: delete existing and re-insert
      await supabase.from('event_staff').delete().eq('event_id', event.id);
      
      if (event.supportStaff && event.supportStaff.length > 0) {
        const staffRecords = event.supportStaff.map(staff => ({
          event_id: event.id,
          name: staff.name,
          role: staff.role || 'Coordinator',
          team_member_id: staff.teamMemberId || null
        }));
        
        await supabase.from('event_staff').insert(staffRecords);
      }

      const index = events.value.findIndex(e => e.id === event.id);
      if (index !== -1) {
        events.value[index] = event;
      }
      AnalyticsEvents.eventUpdated({ event_id: event.id });
    } catch (err) {
      error.value = 'Failed to update event';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateEventNotes(eventId, fields) {
    try {
      const updateData = {};
      if ('notes' in fields) updateData.notes = fields.notes || null;
      if ('impactNotes' in fields) updateData.impact_notes = fields.impactNotes || null;

      const { error: err } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', eventId);

      if (err) throw err;

      const index = events.value.findIndex(e => e.id === eventId);
      if (index !== -1) {
        if ('notes' in fields) events.value[index].notes = fields.notes;
        if ('impactNotes' in fields) events.value[index].impactNotes = fields.impactNotes;
      }
    } catch (err) {
      console.error('Error updating event notes:', err);
      throw err;
    }
  }

  async function deleteEvent(id) {
    try {
      loading.value = true;
      error.value = null;

      // Soft delete using RPC to bypass potential RLS issues
      const { error: err } = await supabase
        .rpc('soft_delete_event', { p_event_id: id });

      if (err) throw err;

      events.value = events.value.filter(event => event.id !== id);
    } catch (err) {
      error.value = 'Failed to delete event';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // Appointment management functions
  async function createAppointment(eventAttendeeId, appointmentData) {
    try {
      const appointmentDate = appointmentData.date;
      
      const { data, error: err } = await supabase
        .from('appointments')
        .insert({
          event_attendee_id: eventAttendeeId,
          calendar_name: appointmentData.calendarName,
          appointment_date: appointmentDate,
          appointment_time: appointmentData.time,
          notes: appointmentData.notes || null
        })
        .select()
        .single();

      if (err) throw err;
      return { data, error: null };
    } catch (err) {
      console.error('Error creating appointment:', err);
      return { data: null, error: err };
    }
  }

  async function updateAppointment(appointmentId, appointmentData) {
    try {
      const appointmentDate = appointmentData.date;
      
      const { data, error: err } = await supabase
        .from('appointments')
        .update({
          calendar_name: appointmentData.calendarName,
          appointment_date: appointmentDate,
          appointment_time: appointmentData.time,
          notes: appointmentData.notes || null
        })
        .eq('id', appointmentId)
        .select()
        .single();

      if (err) throw err;
      return { data, error: null };
    } catch (err) {
      console.error('Error updating appointment:', err);
      return { data: null, error: err };
    }
  }

  async function deleteAppointment(appointmentId) {
    try {
      // Soft delete using RPC to bypass RLS issues
      // The appointments RLS policy has nested subqueries against event_attendees and events
      const { error: err } = await supabase
        .rpc('soft_delete_appointment', { p_appointment_id: appointmentId });

      if (err) throw err;
      return { error: null };
    } catch (err) {
      console.error('Error deleting appointment:', err);
      return { error: err };
    }
  }

  async function getEventAttendeeAppointments(eventAttendeeId) {
    try {
      const { data, error: err } = await supabase
        .from('appointments')
        .select('*')
        .eq('event_attendee_id', eventAttendeeId)
        .eq('_deleted', false)
        .order('appointment_date', { ascending: true });

      if (err) throw err;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error fetching appointments:', err);
      return { data: [], error: err };
    }
  }

  async function getEventAppointments(eventId) {
    try {
      const { data, error: err } = await supabase
        .from('appointments')
        .select(`
          *,
          event_attendees!inner (
            event_id,
            attendee_id,
            attendees!inner (
              id,
              first_name,
              last_name,
              email,
              phone_number
            )
          )
        `)
        .eq('event_attendees.event_id', eventId)
        .eq('_deleted', false)
        .order('appointment_date', { ascending: true });

      if (err) throw err;
      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error fetching event appointments:', err);
      return { data: [], error: err };
    }
  }

  async function sendEventReminder(eventId, daysUntil) {
    try {
      loading.value = true;
      error.value = null;

      const authStore = useAuthStore();
      if (!authStore.user) throw new Error('User not authenticated');

      // Import the notification service dynamically
      const { NotificationService } = await import('../services/notifications');
      const notificationService = new NotificationService();

      // Send reminders to all registered attendees
      const result = await notificationService.sendEventReminders(eventId, daysUntil);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update the event with reminder tracking information
      const fieldName = daysUntil === 3 ? 'reminder_3day_sent_at' : 'reminder_1day_sent_at';
      const byFieldName = daysUntil === 3 ? 'reminder_3day_sent_by' : 'reminder_1day_sent_by';

      const { error: updateErr } = await supabase
        .from('events')
        .update({
          [fieldName]: new Date().toISOString(),
          [byFieldName]: authStore.user.id
        })
        .eq('id', eventId);

      if (updateErr) throw updateErr;

      // Update the local events array
      const eventIndex = events.value.findIndex(e => e.id === eventId);
      if (eventIndex !== -1) {
        events.value[eventIndex][fieldName] = new Date().toISOString();
        events.value[eventIndex][byFieldName] = authStore.user.id;
      }

      return result;
    } catch (err) {
      error.value = 'Failed to send event reminders';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    events,
    loading,
    error,
    fetchEvents,
    refreshEvents,
    createEvent,
    updateEvent,
    updateEventNotes,
    deleteEvent,
    getEventAttendees,
    updateEventAttendee,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    getEventAttendeeAppointments,
    getEventAppointments,
    sendEventReminder
  };
});
